<template>
  <div ref="root" class="font-base">
    <!-- Lime Hamburger button (-> X when open).
         Groesse 1:1 nach live gemessen: 45x42 auf Mobil, ab 768px 66x60.
         Die Farben laufen ueber Variablen, weil Referenzseiten das Quadrat in
         die Kundenfarbe umfaerben (live: .nav-trigger.icon-burger). Ohne
         gepflegte Kundenfarbe greift der Lime-Default. -->
    <button
      ref="burger"
      class="relative flex h-[42px] w-[45px] shrink-0 items-center bg-[color:var(--wg-ref-burger,#b1ca34)] md:h-[60px] md:w-[66px]"
      :aria-expanded="isMenuOpen"
      :aria-label="isMenuOpen ? 'Menü schließen' : 'Menü öffnen'"
      @click="toggleMenu"
    >
      <!-- Balken 1:1 live (.nav-trigger.icon-burger span[aria-hidden]):
           Mobil 27x23 bei 10px Einzug, ab 768px 32x31 bei 17px Einzug, je 3px
           Balken mit gleichen Luecken (7px bzw. 11px). Der mittlere Balken
           sitzt auf festem Pixelwert statt top-1/2 + translate — das landete
           auf einem halben Pixel und liess die Luecken ungleich wirken. -->
      <span class="relative ml-[10px] block h-[23px] w-[27px] md:ml-[17px] md:h-[31px] md:w-8">
        <span
          class="absolute inset-x-0 h-[3px] bg-[color:var(--wg-ref-burger-bars,#163240)] transition-all duration-300 ease-in-out"
          :class="isMenuOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'"
        ></span>
        <span
          class="absolute inset-x-0 top-[10px] h-[3px] bg-[color:var(--wg-ref-burger-bars,#163240)] transition-opacity duration-300 ease-in-out md:top-[14px]"
          :class="isMenuOpen ? 'opacity-0' : 'opacity-100'"
        ></span>
        <span
          class="absolute inset-x-0 h-[3px] bg-[color:var(--wg-ref-burger-bars,#163240)] transition-all duration-300 ease-in-out"
          :class="isMenuOpen ? 'bottom-1/2 translate-y-1/2 -rotate-45' : 'bottom-0'"
        ></span>
      </span>
    </button>

    <!-- Full-screen overlay menu (single left-indented column); slides in from the
         top, emerging from behind the fixed header (header z-50 > overlay z-40). -->
    <Teleport to="body">
    <Transition name="slidedown">
    <div
      v-if="isMenuOpen"
      ref="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Hauptmenü"
      class="fixed inset-0 z-40 overflow-y-auto"
      :style="{ backgroundColor: 'rgba(22, 50, 64, 0.95)' }"
    >
      <!-- Masse 1:1 an der offenen Live-Navigation gemessen (390/640/768/1024/
           1440), nicht geschaetzt:

             Titel     30px introheadh-base, lh 36px, uppercase, ls 1px
             Untertitel  ab 768px sichtbar, darunter display:none
             a-Padding 15px 29px 15px 36px  (<768)
                       15px 36px            (>=768)
             Zeilenhoehe je Eintrag 66px (<768) / 96px (>=768)

           Unter 768px zeigt live zusaetzlich einen Lime-Chevron als Listen-
           Marker — daher hier das eigene Span, das ab md wieder verschwindet.
           Vorher stand der Abstand auf py-5/py-[30px]: 126px pro Eintrag statt
           96px, und der Untertitel lief auch auf dem Handy mit. -->
      <nav class="container pb-10 xl:pb-16" :style="{ paddingTop: headerHeight + 'px' }">
        <ul class="mt-[50px] xl:pl-[25.4%]">
          <li
            v-for="item in menuItems"
            :key="item.title"
          >
            <component
              :is="item.url && item.url !== '#' ? 'a' : 'span'"
              :href="item.url && item.url !== '#' ? item.url : null"
              class="group relative block py-[15px] pl-[66px] pr-[29px] transition-colors md:px-9"
              :class="(item.isActive || item.hasActiveChild) ? 'text-[#b1ca34]' : 'text-white hover:text-[#b1ca34]'"
              :aria-current="item.isActive ? 'page' : null"
              @click="handleNavClick"
            >
              <span
                class="absolute left-9 top-[15px] font-accent-1 text-[30px] leading-9 text-[#b1ca34] md:hidden"
                aria-hidden="true"
              >›</span>
              <span
                class="block font-accent-1 font-normal text-[30px] uppercase leading-9 tracking-[1px]"
              >{{ item.title }}</span>
              <!-- Farben 1:1 live (#mainnav li > a): Titel UND Untertitel erben
                   vom Link — weiss, bei Hover/aktiv komplett #b1ca34. Nur der
                   Pfeil (live pfeil_gruen.svg als ::after) bleibt immer lime. -->
              <span
                v-if="item.teaser"
                class="hidden md:block"
              >{{ item.teaser }} <span class="text-[#b1ca34]" aria-hidden="true">›</span></span>
            </component>
          </li>
        </ul>
      </nav>
    </div>
    </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted } from "vue";

const props = defineProps({
  navigationItems: { type: String, default: null },
  // Logo-URL pro Site (via data-logo); aktuell ungenutzt, aber API-kompatibel.
  logo: { type: String, default: null },
  // Helle Logo-Variante für das offene (dunkle) Menü; dunkle Wortmarke wäre dort unsichtbar.
  logoLight: { type: String, default: null },
});

const root = ref(null);
const burger = ref(null);
const overlay = ref(null);
const isMenuOpen = ref(false);
const menuItems = ref([]);
const headerHeight = ref(0);

let resizeHandler = null;
let keydownHandler = null;
// Elemente, die wir beim Öffnen stillgelegt haben — beim Schließen exakt diese
// wieder freigeben (nicht pauschal alle, sonst reißen wir fremdes inert auf).
let inertedElements = [];

// Active states are recomputed from the URL so they stay correct regardless of
// where the page is rendered/cached (mirrors the BaseSite Navigation logic).
function setActiveStatesBasedOnUrl() {
  const currentPath = window.location.pathname;

  menuItems.value.forEach((item) => {
    item.isActive = currentPath === item.url;
    item.hasActiveChild = false;

    if (item.children && item.children.length > 0) {
      item.children.forEach((child) => {
        child.isActive = currentPath === child.url;
        child.hasActiveChild = false;

        if (child.children && child.children.length > 0) {
          child.children.forEach((subChild) => {
            subChild.isActive = currentPath === subChild.url;
            if (subChild.isActive) {
              child.hasActiveChild = true;
              item.hasActiveChild = true;
            }
          });
        }
        if (child.isActive) {
          item.hasActiveChild = true;
        }
      });
    }
  });
}

function measureHeader() {
  const header = root.value && root.value.closest("header");
  headerHeight.value = header ? header.offsetHeight : 0;
}

function updateBodyScroll() {
  document.body.style.overflow = isMenuOpen.value ? "hidden" : "";
}

// When the menu is open the dark overlay covers the whole viewport (incl. behind
// the header). Make the header itself transparent and swap the logo to its light
// variant so the controls stay visible on the dark teal — like the live site.
function applyHeaderOpenState(open) {
  const header = root.value && root.value.closest("header");
  if (!header) return;
  // Live tauscht bei offenem Menue auf logo_weiss/logo_klein_weiss: Logo
  // komplett weiss, "WG" ausgestanzt. Unser Logo ist inline -> per Klasse (app.css).
  header.classList.toggle("wg-nav-open", open);
  if (open) {
    header.style.backgroundColor = "transparent";
    header.style.boxShadow = "none";
  } else {
    header.style.backgroundColor = "";
    header.style.boxShadow = "";
  }
  const img = header.querySelector('a[href="/"] img');
  if (img && props.logoLight) {
    if (open) {
      if (!img.dataset.prevSrc) img.dataset.prevSrc = img.getAttribute("src");
      img.setAttribute("src", props.logoLight);
    } else if (img.dataset.prevSrc) {
      img.setAttribute("src", img.dataset.prevSrc);
      delete img.dataset.prevSrc;
    }
  }
}

// --- Fokusfalle -----------------------------------------------------------
// Das Overlay wird per Teleport an <body> gehängt, der Burger-Button bleibt im
// Header. Ohne Fokusfalle wandert der Tab-Fokus hinter das Overlay in Inhalte,
// die niemand sehen kann. Der Burger ist im geöffneten Zustand der Schließen-
// Knopf und gehört deshalb MIT in den Zyklus.

function topLevelAncestor(el) {
  let node = el;
  while (node && node.parentElement && node.parentElement !== document.body) {
    node = node.parentElement;
  }
  return node;
}

// Alles außerhalb von Overlay und Header stilllegen. `inert` nimmt den Teilbaum
// aus Fokus und Screenreader; aria-hidden ist der Fallback für Browser ohne
// inert-Unterstützung.
//
// Es reicht NICHT, nur die direkten body-Kinder zu betrachten und dasjenige mit
// dem Header auszunehmen: Header und Hauptinhalt liegen hier im selben Wrapper,
// damit bliebe die ganze Seite fokussierbar. `inert` lässt sich außerdem weiter
// unten im Baum nicht wieder aufheben. Deshalb entlang der Ahnenkette des
// Headers hochlaufen und auf jeder Ebene die GESCHWISTER stilllegen — übrig
// bleibt genau der Pfad zum Header plus das Overlay.
function setBackgroundInert(on) {
  if (!on) {
    inertedElements.forEach((el) => {
      el.removeAttribute("inert");
      el.removeAttribute("aria-hidden");
    });
    inertedElements = [];
    return;
  }

  const keep = root.value ? root.value.closest("header") || root.value : null;
  if (!keep) return;

  let node = keep;
  while (node && node !== document.body && node.parentElement) {
    const parent = node.parentElement;
    Array.from(parent.children).forEach((sibling) => {
      if (sibling === node || sibling === overlay.value) return;
      if (sibling.hasAttribute("inert") || sibling.getAttribute("aria-hidden") === "true") return;
      sibling.setAttribute("inert", "");
      sibling.setAttribute("aria-hidden", "true");
      inertedElements.push(sibling);
    });
    node = parent;
  }
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Erreichbar bleibt, was bei offenem Menü SICHTBAR ist: der Header (Logo,
// Telefon, Burger) und das Overlay. Alles andere ist inert. Die Reihenfolge muss
// der DOM-Reihenfolge entsprechen, sonst kämpft die Falle gegen die native
// Tab-Reihenfolge — dann pendelt der Fokus zwischen zwei Elementen, statt zu
// zyklieren. querySelectorAll liefert Dokumentreihenfolge, deshalb je Wurzel
// einsammeln und aneinanderhängen (Header steht im DOM vor dem Overlay).
function focusableElements() {
  const roots = [];
  // Nur der Header selbst — NICHT sein Wrapper, in dem auch der Hauptinhalt hängt.
  const headerRoot = root.value ? root.value.closest("header") || root.value : null;
  if (headerRoot) roots.push(headerRoot);
  if (overlay.value) roots.push(overlay.value);

  const list = [];
  roots.forEach((r) => {
    list.push(...Array.from(r.querySelectorAll(FOCUSABLE_SELECTOR)));
  });
  // Unsichtbares (z. B. per Breakpoint ausgeblendete Header-Elemente) raus.
  return list.filter((el) => el === burger.value || el.offsetParent !== null);
}

function handleTab(e) {
  const els = focusableElements();
  if (els.length === 0) return;
  const first = els[0];
  const last = els[els.length - 1];
  const active = document.activeElement;
  const inside = els.indexOf(active) !== -1;

  if (e.shiftKey) {
    // Rückwärts vom ersten Element (oder von außerhalb) ans Ende springen.
    if (!inside || active === first) {
      e.preventDefault();
      last.focus();
    }
  } else if (!inside || active === last) {
    e.preventDefault();
    first.focus();
  }
}

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
  if (isMenuOpen.value) measureHeader();
  applyHeaderOpenState(isMenuOpen.value);
  updateBodyScroll();

  if (isMenuOpen.value) {
    // Overlay existiert erst nach dem Render-Tick.
    nextTick(() => {
      setBackgroundInert(true);
      const links = overlay.value ? overlay.value.querySelectorAll(FOCUSABLE_SELECTOR) : [];
      if (links.length > 0) links[0].focus();
      else if (burger.value) burger.value.focus();
    });
  } else {
    setBackgroundInert(false);
    if (burger.value) burger.value.focus();
  }
}

function closeMenu() {
  if (!isMenuOpen.value) return;
  isMenuOpen.value = false;
  applyHeaderOpenState(false);
  updateBodyScroll();
  setBackgroundInert(false);
  // Fokus zurück auf den auslösenden Knopf — sonst landet er am Seitenanfang.
  if (burger.value) burger.value.focus();
}

function handleNavClick() {
  closeMenu();
}

// equivalent to created(): parse the navigation data passed in from Neos
if (props.navigationItems) {
  try {
    const items = JSON.parse(props.navigationItems);
    menuItems.value = Array.isArray(items.menuItems) ? items.menuItems : [];
    setActiveStatesBasedOnUrl();
  } catch (e) {
    console.error("[WGNavigation] Failed to parse navigation items:", e);
  }
} else {
  console.warn("[WGNavigation] No navigation data provided from Neos");
}

onMounted(() => {
  measureHeader();
  resizeHandler = () => measureHeader();
  window.addEventListener("resize", resizeHandler);

  keydownHandler = (e) => {
    if (!isMenuOpen.value) return;
    if (e.key === "Escape") {
      closeMenu();
    } else if (e.key === "Tab") {
      handleTab(e);
    }
  };
  document.addEventListener("keydown", keydownHandler);
});

onUnmounted(() => {
  document.body.style.overflow = "";
  applyHeaderOpenState(false);
  // Sonst bliebe die halbe Seite stillgelegt zurück.
  setBackgroundInert(false);
  if (resizeHandler) window.removeEventListener("resize", resizeHandler);
  if (keydownHandler) document.removeEventListener("keydown", keydownHandler);
});
</script>

<style scoped>
/* Slide the overlay in from the top — it starts shifted up by its own height
   (hidden behind the fixed header) and eases down into place. */
.slidedown-enter-active,
.slidedown-leave-active {
  transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}
.slidedown-enter-from,
.slidedown-leave-to {
  transform: translateY(-100%);
}
@media (prefers-reduced-motion: reduce) {
  .slidedown-enter-active,
  .slidedown-leave-active {
    transition: none;
  }
}
</style>
